import { MarkerType } from "reactflow";

/**
 * Transforms a hierarchical AgentExecutionNode tree into flat ReactFlow nodes and edges.
 * @param {Object} tree - The root AgentExecutionNode from the API
 * @param {string} currentRequestId - The request ID that was originally selected (for highlighting)
 * @returns {{ nodes: Array, edges: Array }} ReactFlow-compatible nodes and edges
 */
export function transformTreeToReactFlow(tree, currentRequestId = null) {
  const nodes = [];
  const edges = [];

  function processNode(node, parentId = null) {
    // Add agent node
    const nodeId = node.requestId;
    nodes.push({
      id: nodeId,
      type: "agentExecution",
      data: {
        requestId: node.requestId,
        agentRole: node.agentRole,
        depth: node.depth,
        durationMs: node.durationMs,
        creditsUsed: node.creditsUsed,
        totalTreeCreditsUsed: node.totalTreeCreditsUsed,
        tokensIn: node.tokensIn,
        tokensOut: node.tokensOut,
        status: node.status,
        errorMessage: node.errorMessage,
        isCurrentRequest: node.requestId === currentRequestId,
        isRoot: node.depth === 0,
        toolCalls: node.toolCalls,
      },
      position: { x: 0, y: 0 }, // Will be set by layout
    });

    // Add edge from parent to this node
    if (parentId) {
      edges.push({
        id: `edge-${parentId}-${nodeId}`,
        source: parentId,
        target: nodeId,
        type: "smoothstep",
        style: { stroke: "#868e96", strokeWidth: 1 },
        markerEnd: { type: MarkerType.ArrowClosed, color: "#868e96" },
      });
    }

    // Process tool calls as intermediate nodes (optional - for detailed view)
    if (node.toolCalls && node.toolCalls.length > 0) {
      for (const toolCall of node.toolCalls) {
        if (toolCall.spawnedRequestId) {
          // This tool spawned a child agent - find the edge we'll create
          // and mark it differently
          const existingEdgeIdx = edges.findIndex(
            (e) =>
              e.source === nodeId && e.target === toolCall.spawnedRequestId
          );
          if (existingEdgeIdx === -1) {
            // The child edge will be created when we process children
            // Store tool info for labeling
          }
        }
      }
    }

    // Recursively process children
    if (node.children) {
      for (const child of node.children) {
        processNode(child, nodeId);
      }
    }
  }

  processNode(tree);

  // Label edges with tool names where we can match them
  const childToToolName = buildChildToToolNameMap(tree);
  for (const edge of edges) {
    const toolName = childToToolName[edge.target];
    if (toolName) {
      edge.label = toolName;
      edge.labelStyle = { fontSize: 10, fill: "#868e96" };
      edge.labelBgStyle = { fill: "#fff", fillOpacity: 0.9 };
      edge.labelBgPadding = [4, 2];
      edge.labelBgBorderRadius = 4;
      edge.style = { stroke: "#fd7e14", strokeWidth: 2 };
      edge.markerEnd = { type: MarkerType.ArrowClosed, color: "#fd7e14" };
    }
  }

  return { nodes, edges };
}

/**
 * Builds a map from child request ID to the tool name that spawned it.
 */
function buildChildToToolNameMap(tree) {
  const map = {};

  function walk(node) {
    if (node.toolCalls) {
      for (const tc of node.toolCalls) {
        if (tc.spawnedRequestId) {
          map[tc.spawnedRequestId] = tc.toolName;
        }
      }
    }
    if (node.children) {
      for (const child of node.children) {
        walk(child);
      }
    }
  }

  walk(tree);
  return map;
}

/**
 * Applies hierarchical layout to ReactFlow nodes.
 * Based on the algorithm from AgentGraphPreview.jsx.
 * @param {Array} nodes - ReactFlow nodes
 * @param {Array} edges - ReactFlow edges
 * @returns {{ nodes: Array, edges: Array }} Nodes with positions set
 */
function estimateNodeHeight(node) {
  const baseHeight = 80;
  const toolCallHeight = 18;
  const toolCount = node.data?.toolCalls?.length || 0;
  return toolCount > 0 ? baseHeight + 12 + toolCount * toolCallHeight : baseHeight;
}

export function getLayoutedElements(nodes, edges) {
  const nodeHeight = 80;
  const horizontalSpacing = 240;
  const verticalSpacing = 140;

  // Build adjacency list and find root node
  const childrenMap = {};
  const hasParent = new Set();

  edges.forEach((edge) => {
    if (!childrenMap[edge.source]) {
      childrenMap[edge.source] = [];
    }
    childrenMap[edge.source].push(edge.target);
    hasParent.add(edge.target);
  });

  // Find root nodes (nodes without parents)
  const rootNodes = nodes.filter((n) => !hasParent.has(n.id));
  if (rootNodes.length === 0 && nodes.length > 0) {
    rootNodes.push(nodes[0]);
  }

  // Calculate depth for each node using BFS
  const depths = {};
  const queue = [...rootNodes.map((n) => ({ id: n.id, depth: 0 }))];
  const visited = new Set();

  while (queue.length > 0) {
    const { id, depth } = queue.shift();
    if (visited.has(id)) continue;
    visited.add(id);
    depths[id] = depth;

    const children = childrenMap[id] || [];
    children.forEach((childId) => {
      if (!visited.has(childId)) {
        queue.push({ id: childId, depth: depth + 1 });
      }
    });
  }

  // Handle any unvisited nodes (disconnected or cycles)
  nodes.forEach((n) => {
    if (depths[n.id] === undefined) {
      depths[n.id] =
        Object.values(depths).length > 0
          ? Math.max(...Object.values(depths)) + 1
          : 0;
    }
  });

  // Group nodes by depth
  const depthGroups = {};
  nodes.forEach((n) => {
    const d = depths[n.id];
    if (!depthGroups[d]) depthGroups[d] = [];
    depthGroups[d].push(n);
  });

  // Calculate cumulative Y offsets per depth based on max node height per row
  const maxDepth = Math.max(0, ...Object.values(depths));
  const depthY = {};
  let cumulativeY = 0;
  for (let d = 0; d <= maxDepth; d++) {
    depthY[d] = cumulativeY;
    const rowNodes = depthGroups[d] || [];
    const maxHeight = rowNodes.reduce(
      (max, n) => Math.max(max, estimateNodeHeight(n)),
      nodeHeight
    );
    cumulativeY += maxHeight + (verticalSpacing - nodeHeight);
  }

  // Position nodes
  const layoutedNodes = nodes.map((node) => {
    const depth = depths[node.id];
    const group = depthGroups[depth];
    const indexInGroup = group.indexOf(node);
    const groupWidth = group.length * horizontalSpacing;
    const startX = -groupWidth / 2 + horizontalSpacing / 2;

    return {
      ...node,
      position: {
        x: startX + indexInGroup * horizontalSpacing,
        y: depthY[depth] ?? depth * verticalSpacing,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
}

/**
 * Calculates summary statistics from the tree.
 */
export function calculateTreeStats(tree) {
  let agentCount = 0;
  let toolCount = 0;
  let totalDuration = 0;
  let totalCredits = 0;
  let maxDepth = 0;

  function walk(node) {
    agentCount++;
    if (node.depth > maxDepth) maxDepth = node.depth;
    if (node.depth === 0) {
      totalDuration = node.durationMs;
      totalCredits = node.totalTreeCreditsUsed;
    }
    toolCount += node.toolCalls?.length || 0;

    if (node.children) {
      for (const child of node.children) {
        walk(child);
      }
    }
  }

  walk(tree);

  return {
    agentCount,
    toolCount,
    totalDuration,
    totalCredits,
    maxDepth,
  };
}
